import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import {
  CheckCircle2,
  Activity,
  Brain,
  Shield,
  Target,
  BarChart3,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import "../styles/about.css";
import SeoHead from "../components/SeoHead";

const experiences = [
  { title: "General Store Manager", start: 2012, end: 2014 },
  { title: "General Manager - Mile High Cab", start: 2014, end: 2016 },
  { title: "General Manager - Green Taxi", start: 2016, end: 2017 },
  { title: "Talent Acquisition Manager", start: 2017, end: 2018 },
  { title: "Co-Owner and Technology/Data Leader - Trans Voyage Taxi", start: 2017, end: null },
  { title: "Manager - LYNX Mobility Data", start: 2021, end: 2026 },
  { title: "Founder and Owner - Old Alex Hub", start: 2022, end: null },
  { title: "Network Success Sr Manager - Big Star Transit", start: 2026, end: null },
];

const AboutMe = () => {
  const [projectStats, setProjectStats] = useState({ total: 0, ai: 0, ops: 0 });
  const currentYear = new Date().getFullYear();
  const getEnd = useCallback((exp) => (exp.end ? exp.end : currentYear), [currentYear]);

  const computeProjectStats = (list) => {
    const aiKeywords = ["ai", "ml", "machine learning", "forecast", "nlp", "automation", "predict", "chatbot"];
    const opsKeywords = ["ops", "operations", "platform", "dispatch", "lifecycle", "management", "reconciliation"];
    const norm = (txt = "") => txt.toLowerCase();
    const ai = list.filter(
      (p) =>
        aiKeywords.some((k) => norm(p.title || "").includes(k)) ||
        aiKeywords.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    const ops = list.filter(
      (p) =>
        opsKeywords.some((k) => norm(p.title || "").includes(k)) ||
        opsKeywords.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    return { total: list.length, ai, ops };
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const resp = await axios.get(API_ENDPOINTS.getArticles);
        if (resp.status === 200 && resp.data?.articles) {
          setProjectStats(computeProjectStats(resp.data.articles));
        }
      } catch (err) {
        // ignore errors; keep defaults
      }
    };
    fetchProjects();
  }, []);

  const { yearRange, cumulativeData, aggregatedYears, yearActiveTitles } = useMemo(() => {
    const minYear = Math.min(...experiences.map((e) => e.start));
    const yrs = [];
    for (let y = minYear; y <= currentYear; y++) yrs.push(y);

    const yearlyRoleCounts = yrs.map((yr) =>
      experiences.reduce((acc, e) => {
        const end = getEnd(e);
        return acc + (e.start <= yr && end >= yr ? 1 : 0);
      }, 0)
    );

    const activeTitles = yrs.map((yr) =>
      experiences
        .filter((e) => {
          const end = getEnd(e);
          return e.start <= yr && end >= yr;
        })
        .map((e) => e.title)
    );

    const cumulative = [];
    yearlyRoleCounts.reduce((acc, val, idx) => {
      const sum = acc + val;
      cumulative[idx] = sum;
      return sum;
    }, 0);

    return {
      yearRange: yrs,
      cumulativeData: cumulative,
      aggregatedYears: cumulative[cumulative.length - 1] || 0,
      yearActiveTitles: activeTitles,
    };
  }, [currentYear, getEnd]);

  const careerTimeline = {
    labels: yearRange.map((y) => String(y)),
    datasets: [
      {
        label: "Cumulative role-years",
        data: cumulativeData,
        borderColor: "#0d6efd",
        backgroundColor: "rgba(13, 110, 253, 0.15)",
        tension: 0.25,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: "#0d6efd",
        pointBorderColor: "rgba(255,255,255,0.7)",
      },
    ],
  };

  const careerOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(16, 24, 40, 0.95)",
        titleColor: "#fff",
        bodyColor: "#d7e0ef",
        borderColor: "rgba(255,255,255,0.08)",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value}`;
          },
          afterBody: function (context) {
            const idx = context[0].dataIndex;
            const titles = yearActiveTitles[idx] || [];
            if (!titles.length) return [];
            const lines = ["Roles:"];
            titles.forEach((t) => lines.push(`- ${t}`));
            return lines;
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year", color: "#6b7280" } },
      y: { beginAtZero: true, title: { display: true, text: "Cumulative role-years" } },
    },
  };

  const strengths = [
    {
      icon: Activity,
      title: "Operational analytics",
      desc: "Provider performance, reconciliation, anomaly detection, and compliance reporting across mobility networks.",
    },
    {
      icon: Brain,
      title: "AI for efficiency",
      desc: "Forecasting, NLP, and automation to reduce bias in ATS flows and speed decision cycles.",
    },
    {
      icon: BarChart3,
      title: "Full-stack delivery",
      desc: "MERN, BI, and data pipelines that put actionable tools in the hands of operations teams quickly.",
    },
    {
      icon: Shield,
      title: "Data governance",
      desc: "Audit-ready controls that support contract compliance, safety, ADA, FTA, and agency reviews.",
    },
  ];

  const focusNow = [
    "Leading transportation network success for Big Star Transit across provider performance, service reliability, and contract discipline.",
    "Supporting paratransit and NEMT operations through analytics, safety coordination, and stakeholder alignment.",
    "Building TaxiOps as an operations platform for taxi and NEMT providers.",
    "Managing Old Alex Hub portfolio work across Trans Voyage Taxi, TaxiOps, mobile products, and After Hours Audio.",
  ];

  const expertise = [
    "Executive leadership, strategic planning, and board-ready reporting.",
    "Data analytics and AI-driven decision making (predictive models, NLP, anomaly detection).",
    "Public policy, compliance, and regulatory management with audit readiness.",
    "Talent acquisition and workforce development for high-performing teams.",
    "Operational optimization, process design, and service quality improvements.",
    "Investment and trading using data-driven strategies.",
  ];

  const interests = [
    "Soccer: Al Ahly SC supporter and midfield player.",
    "Music: Mohamed Mounir, Bob Marley, Aerosmith, Cairokee, Queen.",
    "Egyptian poetry: Abdel Rahman al-Abnoudy, Salah Jahin, Ahmed Fouad Negm.",
    "Coding: Full-stack JavaScript with a focus on MERN applications.",
    "World politics: Governance, policy, and global relations.",
    "Data science: R, Python, and BI for analytics and dashboards.",
    "Machine learning and AI: Deep learning, NLP, and automation.",
  ];

  const businesses = [
    {
      heading: "Ones I own",
      items: [
        "Trans Voyage Taxi: Co-founder; built dispatch, compliance, and growth systems.",
        "Old Alex Hub, LLC: Ventures in AI, SaaS, media, real estate, and tech-focused investing.",
      ],
    },
    {
      heading: "Ones I helped start",
      items: [
        "Denver Taxi and Alpine Taxi: Advisor for business development and strategy.",
        "Green Taxi Cooperative: Launched operations, manuals, and hiring processes.",
      ],
    },
    {
      heading: "Ones I reformed",
      items: [
        "Mile High Cab, Inc.: Led restructuring, hiring improvements, and operational reform at age 22.",
      ],
    },
  ];

  return (
    <div className="about-page">
      <SeoHead
        title="About Mohamed Gad - Transportation, Mobility, Analytics, and Operations"
        description="About Mohamed Gad: transportation and mobility leader focused on network success, analytics, business intelligence, compliance, data governance, and accountable operations."
        keywords={[
          "Mohamed Gad",
          "AI strategist",
          "data scientist",
          "transportation analytics",
          "mobility data leader",
          "ethical AI",
        ]}
        canonical="https://www.mohamedgad.com/aboutme"
        type="profile"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Mohamed Gad",
            "description": "Transportation and mobility leader focused on network success, analytics, business intelligence, compliance, data governance, and accountable operations.",
            "url": "https://www.mohamedgad.com/aboutme",
            "image": {
              "@type": "ImageObject",
              "url": "https://www.mohamedgad.com/mohamed-gad.png"
            },
            "sameAs": [
              "https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/",
              "https://github.com/OldAlexhub",
              "https://www.facebook.com/MohamedMMagdy"
            ],
            "jobTitle": "Network Success Sr Manager - East",
            "worksFor": {
              "@type": "Organization",
              "name": "Big Star Transit, LLC"
            },
            "alumniOf": [
              {
                "@type": "EducationalOrganization",
                "name": "Master of Science in Data Science"
              },
              {
                "@type": "EducationalOrganization",
                "name": "Bachelor of Arts in Political Science"
              }
            ],
            "knowsAbout": [
              "transportation analytics",
              "data science",
              "machine learning",
              "governance",
              "mobility operations",
              "business strategy"
            ],
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.mohamedgad.com/aboutme"
            }
          }
        ]}
      />
      <section className="about-hero container">
        <div className="about-hero-card">
          <p className="eyebrow">About</p>
          <h1 className="display-6 fw-bold">Shaping accountable mobility and AI systems</h1>
          <p className="lead">
            Transportation and mobility leader blending provider network operations, BI, analytics, compliance,
            data governance, and founder-level execution. I build systems that improve accountability, withstand
            audits, and help teams make faster operational decisions.
          </p>
          <div className="about-tags">
            <span className="tag">Operational analytics</span>
            <span className="tag">AI for efficiency</span>
            <span className="tag">Data governance</span>
            <span className="tag">Founder mindset</span>
          </div>
          <div className="about-hero-metrics">
            <div className="metric-card">
              <div className="metric-value">{aggregatedYears}+</div>
              <div className="metric-label">Aggregated role-years</div>
              <div className="metric-note">
                Cumulative, not linear calendar time. Concurrent roles like Trans Voyage Taxi, Old Alex Hub, and transportation leadership roles are
                counted in parallel.
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-value">56k+</div>
              <div className="metric-label">Monthly trips governed</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{projectStats.ai || "-"}</div>
              <div className="metric-label">AI/ML automations</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{projectStats.ops || "-"}</div>
              <div className="metric-label">Ops platforms and systems</div>
            </div>
          </div>
        </div>
      </section>

      <section className="container my-5">
        <div className="section-heading d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <p className="eyebrow">Core strengths</p>
            <h2 className="fw-bold mb-0">Where I create leverage</h2>
          </div>
        </div>
        <div className="row g-4">
          {strengths.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="col-md-6 col-lg-3">
                <div className="info-card h-100">
                  <div className="icon-pill">
                    <Icon size={18} />
                  </div>
                  <h5 className="mb-1">{s.title}</h5>
                  <p className="text-muted mb-0">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="container my-5">
        <div className="row g-4 align-items-center">
          <div className="col-lg-6">
            <div className="card chart-card shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center mb-2">
                  <BarChart3 size={18} className="me-2 text-primary" />
                  <div>
                    <p className="eyebrow mb-0">Trajectory</p>
                    <h5 className="mb-0">Career progression</h5>
                  </div>
                </div>
                <Line data={careerTimeline} options={careerOptions} />
                <p className="chart-note mb-0 mt-3">
                  This curve reflects cumulative role-years rather than a simple count of calendar years.
                  Overlapping leadership tracks, including Trans Voyage Taxi, Old Alex Hub, and transportation roles, increase the total.
                </p>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="info-card h-100">
              <div className="d-flex align-items-center mb-2">
                <CheckCircle2 size={18} className="me-2 text-primary" />
                <h5 className="mb-0">Expertise</h5>
              </div>
              <ul className="list-unstyled mb-0">
                {expertise.map((item) => (
                  <li key={item} className="mb-2 d-flex">
                    <CheckCircle2 size={16} className="me-2 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="container my-5">
        <div className="row g-4">
          <div className="col-lg-6">
            <div className="info-card h-100">
              <p className="eyebrow mb-1">Right now</p>
              <h4 className="mb-3">What I am focused on</h4>
              <ul className="list-unstyled mb-0">
                {focusNow.map((item) => (
                  <li key={item} className="mb-2 d-flex about-focus-item">
                    <Target size={16} className="me-2 text-primary about-list-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="info-card h-100">
              <p className="eyebrow mb-1">Hobbies and interests</p>
              <h4 className="mb-3">Curiosity outside of work</h4>
              <ul className="list-unstyled mb-0">
                {interests.map((item) => (
                  <li key={item} className="mb-2 d-flex">
                    <Sparkles size={16} className="me-2 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="container my-5">
        <div className="row g-4">
          <div className="col-md-6">
            <div className="info-card h-100">
              <p className="eyebrow mb-1">Education</p>
              <h4 className="mb-3">Academic foundation</h4>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <strong>Master of Science in Data Science</strong> (2024-2025) - Completed
                </li>
                <li>
                  <strong>Bachelor of Arts in Political Science</strong> (2014-2020) - Completed
                </li>
              </ul>
            </div>
          </div>
          <div className="col-md-6">
            <div className="info-card h-100">
              <p className="eyebrow mb-1">Awards</p>
              <h4 className="mb-3">Recognition</h4>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  Certificate of Appreciation - Denver International Airport (2019)
                </li>
                <li>
                  David Langston Excellence Award - Central Florida Community College (2009)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="container my-5">
        <div className="info-card">
          <p className="eyebrow mb-1">Businesses</p>
          <h4 className="mb-3">Building and reforming companies</h4>
          <div className="row g-4">
            {businesses.map((b) => (
              <div key={b.heading} className="col-md-4">
                <h6 className="mb-2">{b.heading}</h6>
                <ul className="list-unstyled mb-0">
                  {b.items.map((item) => (
                    <li key={item} className="mb-2 d-flex">
                      <CheckCircle2 size={16} className="me-2 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container text-center my-5">
        <h3>Philosophy</h3>
        <blockquote className="philosophy-text mx-auto">
          AI and data science should empower decision making, not dictate it. The real value comes when systems are
          transparent, auditable, and built with human judgment in the loop.
        </blockquote>
      </section>
    </div>
  );
};

export default AboutMe;
